import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type {
  AIDispatchResponse,
  AIJobStatusResponse,
  AIModel,
  AiBudgetStatus,
  MarketBandDispatchResponse,
  FactoryPacketExtractResponse,
  HinDuplicateResponse,
} from '@marine/contracts/types/ai.types';
import type { ProvenanceMap } from '../types/boat.types';
import type {
  AnalyzeDocumentsInput,
  AnalyzeDocumentsResult,
  ApplySuggestedUpdatesResult,
  DecideSuggestionsInput,
  DismissSuggestedUpdatesResult,
  SuggestedUpdates,
  SuggestionItemType,
} from '../types/suggestedUpdates.types';

/**
 * Phase 40-03 UX-04 — description tone + length vocabulary (locked per
 * CONTEXT D-15). Forwarded in the POST body to the Laravel description
 * dispatcher which threads them through to the ai-engine description
 * agent's prompt template.
 */
export type DescriptionTone = 'professional' | 'casual' | 'family';
export type DescriptionLength = 'short' | 'medium' | 'long';

export interface GenerateDescriptionOptions {
  tone?: DescriptionTone;
  length?: DescriptionLength;
}

export const aiApi = {
  // Dispatch operations
  analyzeImage: (boatId: string) =>
    tenantApi.postOne<AIDispatchResponse>(`/ai/boats/${boatId}/analyze-image`),

  lookupSpecs: (boatId: string) =>
    tenantApi.postOne<AIDispatchResponse>(`/ai/boats/${boatId}/lookup-specs`),

  /**
   * Generate a long-form sales description for the boat. Optionally
   * accepts a locked tone + length vocabulary (UX-04) threaded through
   * to the dispatcher. When called with no opts, the backend falls back
   * to its defaults (tone=professional, length=medium — see
   * LocationDescriptionDispatcher.php + DescriptionDispatcher.php on the
   * Laravel side).
   */
  generateDescription: (boatId: string, opts?: GenerateDescriptionOptions) =>
    tenantApi.postOne<AIDispatchResponse>(
      `/ai/boats/${boatId}/generate-description`,
      opts ?? {},
    ),

  fetchComparables: (boatId: string) =>
    tenantApi.postOne<AIDispatchResponse>(`/ai/boats/${boatId}/fetch-comparables`),

  generateSeoMetadata: (boatId: string) =>
    tenantApi.postOne<AIDispatchResponse>(`/ai/boats/${boatId}/generate-seo-metadata`),

  /**
   * Phase 44 — PRICE-01 / PRICE-02 (D-07 / D-15).
   *
   * Dispatch the market-band pricing agent for a SAVED inventory item.
   * Unlike the per-boat AI endpoints above (which take a path segment), the
   * Laravel route is `/ai/market-band` (see backend routes/Inventory.php
   * Route::post('market-band', ...)) — the inventory id rides in the JSON body
   * because market-band keys on stored comparables which require a persisted
   * row (44-CONTEXT Pitfall #8: pre-save drafts cannot produce a suggestion).
   *
   * Callers MUST pass a non-null boatId — the hook layer (`useMarketBand`)
   * guards on null. The dispatcher returns a 202 ACK with a job_id; terminal
   * result arrives via Centrifugo + `useAIJobStatus(jobId)`.
   */
  dispatchMarketBand: (boatId: string) =>
    tenantApi.postOne<MarketBandDispatchResponse>('/ai/market-band', {
      inventory_item_id: boatId,
    }),

  /**
   * Phase 45.5 — INGEST-01 (D-04 / D-16). Trigger the factory-packet
   * orchestrator for a SAVED boat's confirmed media set. Fans out the
   * packet-router → 3 extractors (parallel) → gap-filler (last) on Laravel; the
   * progressive fill streams back over Centrifugo `ai-stream.{tenant}.{job}`
   * (consumed by the canvas). The inventory id rides in the JSON body (the
   * orchestrator keys on the persisted media collection). Returns a 202 ACK.
   */
  extractFactoryPacket: (boatId: string) =>
    tenantApi.postOne<FactoryPacketExtractResponse>('/ai/factory-packet/extract', {
      inventory_item_id: boatId,
    }),

  /**
   * Phase 45.5 — INGEST-01 (D-18). The HIN-duplicate guard: does this HIN
   * already exist in the tenant's inventory? Powers the slim "This HIN is
   * already in your inventory" notice before the dealer creates a duplicate.
   */
  checkHinDuplicate: (hin: string, excludeBoatId?: string) =>
    tenantApi.getOne<HinDuplicateResponse>(
      `/ai/factory-packet/hin-duplicate?hin=${encodeURIComponent(hin)}` +
        (excludeBoatId ? `&exclude_boat_id=${encodeURIComponent(excludeBoatId)}` : ''),
    ),

  /**
   * Phase 45.5.2 sub-plan 5 — field-sources projection (Task 11). Returns the
   * canonical ProvenanceMap for a boat from the dedicated
   * `inventory_field_sources` projection (backfilled for legacy drafts by
   * sub-plan 1). Covers both new drafts (written by the factory-packet
   * orchestrator) and legacy drafts (backfilled from ai_meta._provenance on
   * sub-plan 1 migration). Used by LiveFillCanvas to hydrate per-field
   * provenance on resume, replacing the ai_meta._provenance read.
   */
  getFieldSources: (boatId: string) =>
    tenantApi.getOne<ProvenanceMap>(`/boats/${boatId}/field-sources`),

  /**
   * #581 Task 9 — the confirm-and-import review read (Task 6 backend). Returns
   * the item's needs_review field rows + pending entity/option suggestions,
   * item-type generic across boats/engines/trailers.
   */
  getSuggestedUpdates: (type: SuggestionItemType, id: string) =>
    tenantApi.getOne<SuggestedUpdates>(`/${type}/${id}/suggested-updates`),

  /**
   * #581 Task 9 — apply the dealer's confirm-and-import selections (Task 7
   * backend). At least one of `field_source_ids` / `suggestion_ids` must be
   * non-empty (server-enforced — DecideSuggestedUpdatesRequest).
   */
  applySuggestedUpdates: (type: SuggestionItemType, id: string, body: DecideSuggestionsInput) =>
    tenantApi.postOne<ApplySuggestedUpdatesResult>(`/${type}/${id}/suggested-updates/apply`, body),

  /**
   * #581 Task 9 — permanently dismiss suggestions the dealer declined
   * (Task 7 backend). Same body shape + auth as apply.
   */
  dismissSuggestedUpdates: (type: SuggestionItemType, id: string, body: DecideSuggestionsInput) =>
    tenantApi.postOne<DismissSuggestedUpdatesResult>(`/${type}/${id}/suggested-updates/dismiss`, body),

  /**
   * #581 Task 9 — the manual "Analyze documents" / re-run trigger (Task 8
   * backend). Always runs in Propose mode server-side — nothing here ever
   * writes canonical fields directly, every differing winner lands as a
   * suggestion. `force` bypasses the already-extracted-media filter.
   */
  analyzeDocuments: (type: SuggestionItemType, id: string, body?: AnalyzeDocumentsInput) =>
    tenantApi.postOne<AnalyzeDocumentsResult>(`/${type}/${id}/documents/analyze`, body ?? {}),

  /**
   * SP2 post-save enrichment — latest AIJobStatus row per operation_type for a
   * boat (`GET /api/ai/boats/{id}/ai-jobs`). `getList` unwraps the
   * `{data: [...]}` envelope, so this returns `AIJobStatusResponse[]` directly
   * (one row per operation_type). Consumed by `useBoatAiJobs` which folds the
   * array into a by-operation map.
   */
  getBoatAiJobs: (boatId: string) =>
    tenantApi.getList<AIJobStatusResponse>(`/ai/boats/${boatId}/ai-jobs`),

  // Job status polling
  getJobStatus: (jobId: string) =>
    tenantApi.getOne<AIJobStatusResponse>(`/ai/jobs/${jobId}`),

  /**
   * #607 — apply a completed AI job's generated result onto the boat record
   * (Task 7 backend). Returns which fields were written vs. skipped (already
   * had content and were left unchanged per the apply-is-additive contract).
   * #895 — `withheld` is the subset of `skipped` that was genuinely withheld
   * because the target already held a value (as opposed to a field the AI
   * simply produced nothing for) — see AiApplyDecision.
   */
  applyJobResult: (boatId: string, jobId: string) =>
    tenantApi.postOne<{ applied: string[]; skipped: string[]; withheld: string[] }>(
      `/ai/boats/${boatId}/ai-jobs/${jobId}/apply`,
    ),

  /**
   * #607 — discard a completed AI job's generated result without applying it
   * (Task 7 backend).
   */
  discardJobResult: (boatId: string, jobId: string) =>
    tenantApi.postOne<{ dismissed: boolean }>(
      `/ai/boats/${boatId}/ai-jobs/${jobId}/discard`,
    ),

  // Models
  getModels: () =>
    tenantApi.getList<AIModel>('/ai/models'),

  refreshModels: () =>
    tenantApi.postOne<AIModel[]>('/ai/models/refresh'),

  // Usage
  checkBudget: () =>
    tenantApi.getOne<AiBudgetStatus>('/ai/usage/check'),
};
